'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { Compass, Crown, Home, MessageCircle, Wand2 } from 'lucide-react';

import { Trans } from '@kit/ui/trans';

const tabs = [
  { icon: Home, labelKey: 'common:mobileNav.home', path: '/' },
  { icon: Compass, labelKey: 'common:mobileNav.catalog', path: '/catalog' },
  { icon: Wand2, labelKey: 'common:mobileNav.create', path: '/create' },
  { icon: MessageCircle, labelKey: 'common:mobileNav.chat', path: '/chat' },
  {
    icon: Crown,
    labelKey: 'common:mobileNav.premium',
    path: '/pricing',
    highlight: true,
  },
];

export function MobileBottomNav() {
  const pathname = usePathname();

  // Hide on active chat pages (chat input is at the bottom)
  if (pathname.match(/^\/chat\/[^/]+/)) {
    return null;
  }

  return (
    <nav className="fixed right-0 bottom-0 left-0 z-50 border-t border-white/10 bg-black/90 backdrop-blur-md lg:hidden">
      {/* Solid skirt below the nav: iOS Safari's collapsing URL bar makes
          fixed-bottom elements lag a few frames while scrolling, flashing
          the page background beneath — this fills that strip with black. */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: '100%',
          height: '100vh',
          background: '#000',
        }}
      />
      <div
        className="flex items-center justify-around px-2 pt-1"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive =
            tab.path === '/' ? pathname === '/' : pathname.startsWith(tab.path);

          if ('highlight' in tab && tab.highlight) {
            // Upgrade glows rather than blocks: a soft feathered gold
            // radiance with a slow glisten, no borders/edges (inline
            // styles — the prod build drops uncommon Tailwind utilities).
            return (
              <Link
                key={tab.path}
                href={tab.path}
                className="relative flex min-w-[64px] flex-col items-center gap-1 overflow-hidden px-4 py-3"
                style={{ borderRadius: 18, color: '#fcd34d' }}
              >
                <style>{`@keyframes upgradeGlisten {
                  0%, 100% { opacity: 0.75; }
                  50% { opacity: 1; }
                }`}</style>
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'radial-gradient(130% 150% at 65% 12%, rgba(250,204,21,0.34), rgba(202,138,4,0.14) 48%, rgba(202,138,4,0) 78%)',
                    animation: 'upgradeGlisten 3.2s ease-in-out infinite',
                    pointerEvents: 'none',
                  }}
                />
                <Icon
                  className="relative h-6 w-6"
                  style={{
                    filter: 'drop-shadow(0 0 6px rgba(250,204,21,0.55))',
                  }}
                />
                <span className="relative text-xs font-semibold">
                  <Trans i18nKey={tab.labelKey} />
                </span>
              </Link>
            );
          }

          return (
            <Link
              key={tab.path}
              href={tab.path}
              className={`flex min-w-[64px] flex-col items-center gap-1 px-4 py-3 transition-colors ${
                isActive ? 'text-eros-pink' : 'text-muted-foreground'
              }`}
            >
              <div className="relative">
                {isActive && (
                  <div className="bg-eros-pink absolute -top-2 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full" />
                )}
                <Icon className="h-6 w-6" />
              </div>
              <span className="text-xs font-medium">
                <Trans i18nKey={tab.labelKey} />
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
